import React from 'react';
import {
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Popover,
  Button,
  Checkbox,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ActivityRow, listActivities, updateActivityStatus } from '../api/activities';
import { listCandidatures } from '../api/candidatures';
import { listUsers } from '../api/users';
import { listPostsSummary } from '../api/posts';

const formatDate = (date?: Date) =>
  date ? date.toLocaleDateString() : '—';

const statusColor = (status?: string) => {
  switch (status) {
    case 'validated':
      return 'success';
    case 'rejected':
      return 'error';
    case 'pending':
    default:
      return 'warning';
  }
};

export const ActivitiesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery<ActivityRow[]>({
    queryKey: ['activities'],
    queryFn: () => listActivities(),
  });

  const { data: candidatures } = useQuery({
    queryKey: ['candidatures-for-activities'],
    queryFn: listCandidatures,
  });
  const { data: users } = useQuery({
    queryKey: ['users-for-activities'],
    queryFn: listUsers,
  });
  const { data: posts } = useQuery({
    queryKey: ['posts-summary-for-activities'],
    queryFn: listPostsSummary,
  });

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<string>('all');
  const [orderBy, setOrderBy] = React.useState<
    'candidature' | 'status' | 'date' | 'hours' | 'createdAt' | 'updatedAt'
  >('date');
  const [order, setOrder] = React.useState<'asc' | 'desc'>('desc');

  const [confirmAnchorEl, setConfirmAnchorEl] =
    React.useState<HTMLElement | null>(null);
  const [confirmDescription, setConfirmDescription] = React.useState('');
  const [pendingActivity, setPendingActivity] = React.useState<{
    ids: string[];
    status: 'validated' | 'rejected';
  } | null>(null);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);

  const confirmOpen = Boolean(confirmAnchorEl);

  const handleRequestSort = (
    property:
      | 'candidature'
      | 'status'
      | 'date'
      | 'hours'
      | 'createdAt'
      | 'updatedAt'
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const userNameById = React.useMemo(() => {
    const map: Record<string, string> = {};
    users?.forEach((u) => {
      const fullName = `${u.lastName ?? ''} ${u.firstName ?? ''}`.trim();
      map[u.id] = fullName || u.email || u.id;
    });
    return map;
  }, [users]);

  const postTitleById = React.useMemo(() => {
    const map: Record<string, string> = {};
    posts?.forEach((p) => {
      map[p.id] = p.title || p.id;
    });
    return map;
  }, [posts]);

  const candidatureLabelById = React.useMemo(() => {
    const map: Record<string, string> = {};
    candidatures?.forEach((c) => {
      const name = c.userId ? userNameById[c.userId] ?? c.userId : '';
      const postTitle = c.postId ? postTitleById[c.postId] ?? c.postId : '';
      const labelParts = [name, postTitle].filter(Boolean);
      if (labelParts.length) {
        map[c.id] = labelParts.join(' · ');
      }
    });
    return map;
  }, [candidatures, userNameById, postTitleById]);

  const filtered = React.useMemo(() => {
    if (!data) return [];
    return data.filter((a) => {
      const candidatureLabel = a.candidatureId
        ? candidatureLabelById[a.candidatureId] ?? a.candidatureId
        : '';
      const haystack = `${candidatureLabel} ${a.description ?? ''}`
        .toLowerCase()
        .trim();
      const matchesSearch = haystack.includes(search.toLowerCase());
      const matchesStatus =
        statusFilter === 'all' || (a.status ?? 'pending') === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [data, search, statusFilter, candidatureLabelById]);

  const sorted = React.useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const factor = order === 'asc' ? 1 : -1;
      let av: string | Date | number | undefined;
      let bv: string | Date | number | undefined;

      const getCandidatureLabel = (x: ActivityRow) =>
        x.candidatureId
          ? candidatureLabelById[x.candidatureId] ?? x.candidatureId
          : '';

      switch (orderBy) {
        case 'candidature':
          av = getCandidatureLabel(a).toLowerCase();
          bv = getCandidatureLabel(b).toLowerCase();
          break;
        case 'status':
          av = (a.status ?? '').toLowerCase();
          bv = (b.status ?? '').toLowerCase();
          break;
        case 'hours':
          av = a.hours ?? 0;
          bv = b.hours ?? 0;
          break;
        case 'date':
          av = a.date ?? new Date(0);
          bv = b.date ?? new Date(0);
          break;
        case 'createdAt':
          av = a.createdAt ?? new Date(0);
          bv = b.createdAt ?? new Date(0);
          break;
        case 'updatedAt':
          av = a.updatedAt ?? new Date(0);
          bv = b.updatedAt ?? new Date(0);
          break;
        default:
          av = 0 as any;
          bv = 0 as any;
      }

      if (av instanceof Date && bv instanceof Date) {
        return (av.getTime() - bv.getTime()) * factor;
      }

      const as = String(av);
      const bs = String(bv);
      if (as < bs) return -1 * factor;
      if (as > bs) return 1 * factor;
      return 0;
    });
    return arr;
  }, [filtered, order, orderBy, candidatureLabelById]);

  const paginated = React.useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  const handleChangeStatusRequest = (
    event: React.MouseEvent<HTMLElement>,
    activityOrIds: ActivityRow | string[],
    status: 'validated' | 'rejected'
  ) => {
    const actionLabel = status === 'validated' ? 'valider' : 'rejeter';

    if (Array.isArray(activityOrIds)) {
      const ids = activityOrIds;
      if (!ids.length) return;
      setConfirmDescription(
        `Voulez-vous vraiment ${actionLabel} les ${ids.length} activités sélectionnées ?`
      );
      setPendingActivity({ ids, status });
    } else {
      const activity = activityOrIds;
      const candidatureLabel = activity.candidatureId
        ? candidatureLabelById[activity.candidatureId] ?? activity.candidatureId
        : 'cette activité';
      setConfirmDescription(
        `Voulez-vous vraiment ${actionLabel} l'activité liée à ${candidatureLabel} ?`
      );
      setPendingActivity({ ids: [activity.id], status });
    }

    setConfirmAnchorEl(event.currentTarget);
  };

  const handleConfirmActivity = async () => {
    if (!pendingActivity) return;
    await Promise.all(
      pendingActivity.ids.map((id) =>
        updateActivityStatus(id, pendingActivity.status)
      )
    );
    await queryClient.invalidateQueries({ queryKey: ['activities'] });
    setConfirmAnchorEl(null);
    setPendingActivity(null);
    setSelectedIds([]);
  };

  const handleCancelActivity = () => {
    setConfirmAnchorEl(null);
    setPendingActivity(null);
  };

  const allSelected =
    filtered.length > 0 &&
    filtered.every((a) => selectedIds.includes(a.id));
  const someSelected =
    filtered.length > 0 &&
    filtered.some((a) => selectedIds.includes(a.id)) &&
    !allSelected;

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(filtered.map((a) => a.id));
    } else {
      setSelectedIds([]);
    }
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Activités
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <TextField
          size="small"
          label="Rechercher"
          placeholder="Candidat, poste, description..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="status-filter-label">Statut</InputLabel>
          <Select
            labelId="status-filter-label"
            label="Statut"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">Tous</MenuItem>
            <MenuItem value="pending">En attente</MenuItem>
            <MenuItem value="validated">Validée</MenuItem>
            <MenuItem value="rejected">Rejetée</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          gap: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {selectedIds.length
            ? `${selectedIds.length} sélectionnée(s)`
            : 'Aucune sélection'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={!selectedIds.length}
            onClick={(event) =>
              handleChangeStatusRequest(event, selectedIds, 'validated')
            }
          >
            Valider la sélection
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={!selectedIds.length}
            onClick={(event) =>
              handleChangeStatusRequest(event, selectedIds, 'rejected')
            }
          >
            Rejeter la sélection
          </Button>
        </Box>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  indeterminate={someSelected}
                  checked={allSelected}
                  onChange={(e) => toggleSelectAll(e.target.checked)}
                />
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'candidature' ? order : false}
              >
                <TableSortLabel
                  active={orderBy === 'candidature'}
                  direction={orderBy === 'candidature' ? order : 'asc'}
                  onClick={() => handleRequestSort('candidature')}
                >
                  Candidat · Poste
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'status' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleRequestSort('status')}
                >
                  Statut
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'hours' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'hours'}
                  direction={orderBy === 'hours' ? order : 'asc'}
                  onClick={() => handleRequestSort('hours')}
                >
                  Heures
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'date' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'date'}
                  direction={orderBy === 'date' ? order : 'asc'}
                  onClick={() => handleRequestSort('date')}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'createdAt' ? order : false}
              >
                <TableSortLabel
                  active={orderBy === 'createdAt'}
                  direction={orderBy === 'createdAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('createdAt')}
                >
                  Créée le
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'updatedAt' ? order : false}
              >
                <TableSortLabel
                  active={orderBy === 'updatedAt'}
                  direction={orderBy === 'updatedAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('updatedAt')}
                >
                  Mise à jour le
                </TableSortLabel>
              </TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={9}>Chargement...</TableCell>
              </TableRow>
            )}
            {!isLoading &&
              paginated.map((a) => (
                <TableRow key={a.id} hover selected={selectedIds.includes(a.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedIds.includes(a.id)}
                      onChange={() => toggleOne(a.id)}
                    />
                  </TableCell>
                  <TableCell>
                    {a.candidatureId
                      ? candidatureLabelById[a.candidatureId] ??
                        a.candidatureId
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={a.status ?? 'pending'}
                      color={statusColor(a.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{a.hours ?? '—'}</TableCell>
                  <TableCell>{formatDate(a.date)}</TableCell>
                  <TableCell>{formatDate(a.createdAt)}</TableCell>
                  <TableCell>{formatDate(a.updatedAt)}</TableCell>
                  <TableCell>{a.description ?? '—'}</TableCell>
                  <TableCell align="right">
                    {a.status !== 'validated' && (
                      <Tooltip title="Valider l'activité">
                        <IconButton
                          size="small"
                          onClick={(event) =>
                            handleChangeStatusRequest(event, a, 'validated')
                          }
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {a.status !== 'rejected' && (
                      <Tooltip title="Rejeter l'activité">
                        <IconButton
                          size="small"
                          onClick={(event) =>
                            handleChangeStatusRequest(event, a, 'rejected')
                          }
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            {!isLoading && !filtered.length && (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography variant="body2" color="text.secondary">
                    Aucune activité trouvée.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
      />
      <Popover
        open={confirmOpen}
        anchorEl={confirmAnchorEl}
        onClose={handleCancelActivity}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, maxWidth: 320 }}>
          <Typography variant="subtitle2" gutterBottom>
            Confirmer l&apos;action
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {confirmDescription}
          </Typography>
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            <Button onClick={handleCancelActivity} color="inherit" size="small">
              Annuler
            </Button>
            <Button
              onClick={handleConfirmActivity}
              color="primary"
              variant="contained"
              size="small"
            >
              Confirmer
            </Button>
          </Box>
        </Box>
      </Popover>
    </Paper>
  );
};

